import type { VisitorConsent } from '__generated__/graphql'

// undefined = Cookiebot not loaded or banner unanswered.
export type ConsentState = 'granted' | 'denied' | undefined
type ConsentCategory = 'statistics' | 'marketing' | 'preferences'

// A true flag counts as granted even without a banner response, so implied /
// out-of-region consent regimes work; 'denied' needs an explicit answer.
export const getCookiebotConsent = (
  category: ConsentCategory
): ConsentState => {
  if (typeof window === 'undefined') return undefined
  const cookiebot = window.Cookiebot
  if (cookiebot?.consent?.[category] === true) return 'granted'
  if (!cookiebot?.hasResponse) return undefined
  return 'denied'
}

const toShopify = (state: ConsentState) =>
  state === undefined ? undefined : state === 'granted'

// Cookiebot categories mapped onto Shopify's purposes for @inContext, so
// checkout inherits the banner's answer. saleOfData follows marketing.
// Unanswered stays undefined so Shopify applies its regional default.
export const getShopifyVisitorConsent = () => {
  const marketing = toShopify(getCookiebotConsent('marketing'))
  return {
    analytics: toShopify(getCookiebotConsent('statistics')),
    marketing,
    preferences: toShopify(getCookiebotConsent('preferences')),
    saleOfData: marketing,
  } satisfies VisitorConsent
}

// Same answer in the Customer Privacy API's setTrackingConsent shape.
export const getShopifyTrackingConsent = (): ShopifyTrackingConsent => {
  const { saleOfData, ...rest } = getShopifyVisitorConsent()
  return { ...rest, sale_of_data: saleOfData }
}

const PURPOSES = [
  'analytics',
  'marketing',
  'preferences',
  'sale_of_data',
] as const

// Shopify reports 'yes' / 'no' / '' (undeclared); same shape as ours.
const fromShopify = (answer: ShopifyConsentAnswer) =>
  answer === '' ? undefined : answer === 'yes'

// true when Shopify already holds this answer.
export const shopifyConsentMatches = (
  current: ShopifyCurrentVisitorConsent,
  next: ShopifyTrackingConsent
): boolean => PURPOSES.every((key) => fromShopify(current[key]) === next[key])
